import { useMemo, useState } from 'react';
import { Button, Card, CardBody, Stepper } from '@vnetwork/ui/primitives';
import { ActionBar } from '@vnetwork/ui/layout';
import { useToast } from '@vnetwork/ui/feedback';
import {
  useAutoAssign,
  useDiscoverCandidates,
  type AutoAssignRequest,
  type AutoAssignResult,
} from '@/api/projects';
import { ConditionsStep } from './auto-discovery/ConditionsStep';
import { PreviewStep } from './auto-discovery/PreviewStep';
import { ResultStep } from './auto-discovery/ResultStep';
import {
  conditionsToFilters,
  conditionsToParams,
  emptyConditions,
  type RowSelection,
  type WizardConditions,
} from './auto-discovery/parts';

const STEPS = [
  { label: 'Conditions', description: 'Define what to gather' },
  { label: 'Preview', description: 'Review matches' },
  { label: 'Result', description: 'Confirm & assign' },
];

const PER_PAGE = 50;

export interface AutoDiscoveryWizardProps {
  /** When provided, the project is fixed and the project picker is hidden. */
  targetProjectId?: string;
  onClose: () => void;
  onDone?: () => void;
}

/**
 * Reusable 3-step wizard to gather CDN domains + compute instances into a
 * project by conditions, with a preview → confirm flow. Self-contained; embed
 * inside a Dialog or page section.
 */
export function AutoDiscoveryWizard({
  targetProjectId,
  onClose,
  onDone,
}: AutoDiscoveryWizardProps) {
  const { show } = useToast();
  const [step, setStep] = useState(0);
  const [conditions, setConditions] = useState<WizardConditions>(() =>
    emptyConditions(targetProjectId),
  );
  const [page, setPage] = useState(1);
  const [rowSelection, setRowSelection] = useState<RowSelection>({});
  const [selectAllMatching, setSelectAllMatching] = useState(false);
  const [dryRunResult, setDryRunResult] = useState<AutoAssignResult | null>(null);
  const [result, setResult] = useState<AutoAssignResult | null>(null);

  const projectId = conditions.projectId;
  const params = useMemo(() => conditionsToParams(conditions, page, PER_PAGE), [conditions, page]);

  const candidates = useDiscoverCandidates(projectId, params, { enabled: step === 1 });
  const data = candidates.data;

  // Preview/assign are gated behind `canPreview` (requires a project), so the
  // mutation never fires until `projectId` is a real id.
  const autoAssign = useAutoAssign(projectId);

  const checkedIds = useMemo(
    () => Object.keys(rowSelection).filter((id) => rowSelection[id]),
    [rowSelection],
  );

  // When select-all is ON, the committed count is the full matched total;
  // otherwise it's the explicitly checked rows on the (current) page.
  const selectedCount = selectAllMatching ? data?.total ?? 0 : checkedIds.length;

  /**
   * Build the auto-assign body. Select-all → filters only (server matches the
   * whole set; we never enumerate ids client-side). Manual → the checked
   * resource_ids as include_ids, still passing filters for server validation.
   */
  function buildRequest(dryRun: boolean): AutoAssignRequest {
    const filters = conditionsToFilters(conditions);
    if (selectAllMatching) {
      return { filters, dry_run: dryRun };
    }
    const includeIds = checkedIds.map((rowId) => rowId.split(':').slice(1).join(':'));
    return { filters, include_ids: includeIds, dry_run: dryRun };
  }

  const canPreview = !!projectId && conditions.resourceTypes.length > 0;
  const canAssign = selectAllMatching ? (data?.total ?? 0) > 0 : checkedIds.length > 0;

  function goPreview() {
    setDryRunResult(null);
    setStep(1);
  }

  function handleDryRun() {
    autoAssign.mutate(buildRequest(true), {
      onSuccess: (res) => setDryRunResult(res),
      onError: () => show({ tone: 'danger', title: 'Dry run failed' }),
    });
  }

  function handleAssign() {
    autoAssign.mutate(buildRequest(false), {
      onSuccess: (res) => {
        setResult(res);
        setStep(2);
        show({ tone: 'success', title: `${res.assigned} resource(s) assigned` });
      },
      onError: () => show({ tone: 'danger', title: 'Assignment failed' }),
    });
  }

  function reset() {
    setStep(0);
    setConditions(emptyConditions(targetProjectId));
    setPage(1);
    setRowSelection({});
    setSelectAllMatching(false);
    setDryRunResult(null);
    setResult(null);
  }

  function renderActions() {
    if (step === 0) {
      return (
        <ActionBar
          start={
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
          }
          end={
            <Button disabled={!canPreview} onClick={goPreview}>
              Next: Preview
            </Button>
          }
        />
      );
    }
    if (step === 1) {
      return (
        <ActionBar
          start={
            <Button variant="ghost" onClick={() => setStep(0)}>
              Back
            </Button>
          }
          end={
            <Button disabled={!canAssign || autoAssign.isPending} onClick={handleAssign}>
              {autoAssign.isPending ? 'Assigning…' : `Assign ${selectedCount}`}
            </Button>
          }
        />
      );
    }
    return (
      <ActionBar
        start={
          <Button variant="ghost" onClick={reset}>
            Assign more
          </Button>
        }
        end={
          <Button
            onClick={() => {
              onDone?.();
              onClose();
            }}
          >
            Done
          </Button>
        }
      />
    );
  }

  return (
    <Card>
      <CardBody>
        <div style={{ display: 'grid', gap: 20 }}>
          <Stepper steps={STEPS} current={step} reached={step} />

          {step === 0 ? (
            <ConditionsStep
              value={conditions}
              onChange={setConditions}
              projectLocked={!!targetProjectId}
            />
          ) : null}

          {step === 1 ? (
            <PreviewStep
              items={data?.items ?? []}
              counts={data?.counts ?? { unassigned: 0, this_project: 0, other_project: 0 }}
              total={data?.total ?? 0}
              page={page}
              perPage={PER_PAGE}
              isLoading={candidates.isLoading}
              isError={candidates.isError}
              rowSelection={rowSelection}
              onRowSelectionChange={setRowSelection}
              selectAllMatching={selectAllMatching}
              onSelectAllMatchingChange={setSelectAllMatching}
              onPageChange={setPage}
              selectedCount={selectedCount}
              onDryRun={handleDryRun}
              dryRunPending={autoAssign.isPending}
              dryRunResult={dryRunResult}
            />
          ) : null}

          {step === 2 && result ? <ResultStep result={result} /> : null}

          {renderActions()}
        </div>
      </CardBody>
    </Card>
  );
}
